// ScoringPopover.jsx
import React, { useRef } from "react";
import {
    Popover,
    Box,
    Typography,
    Button,
} from "@material-ui/core";

/**
 * Botón con long press (mouse + touch)
 * - Tap/click => onClick
 * - Long press => onLongPress
 *
 * IMPORTANT: no usa hooks en ScoringPopover, así evitamos:
 * "Rendered fewer hooks than expected"
 */
const LongPressButton = ({
    onClick,
    onLongPress,
    ms = 450,
    disabled = false,
    children,
    ...btnProps
}) => {
    const timerRef = useRef(null);
    const longPressTriggeredRef = useRef(false);

    const clear = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
    };

    const start = (e) => {
        if (disabled) return;

        longPressTriggeredRef.current = false;
        clear();

        // Evita menú contextual / selección rara (sin romper scroll normalmente)
        if (e?.preventDefault) e.preventDefault();

        timerRef.current = setTimeout(() => {
            longPressTriggeredRef.current = true;
            onLongPress?.();
        }, ms);
    };

    const end = () => {
        if (disabled) return;

        clear();

        // Si NO se disparó long press => click normal
        if (!longPressTriggeredRef.current) {
            onClick?.();
        }

        longPressTriggeredRef.current = false;
    };

    const cancel = () => {
        clear();
        longPressTriggeredRef.current = false;
    };

    return (
        <Button
            {...btnProps}
            disabled={disabled}
            onMouseDown={start}
            onMouseUp={end}
            onMouseLeave={cancel}
            onTouchStart={start}
            onTouchEnd={end}
            onTouchCancel={cancel}
            onContextMenu={(e) => e.preventDefault()}
        >
            {children}
        </Button>
    );
};

const ScoringPopover = ({
    anchorEl,
    onClose,
    onScore,
    playerData,
    playerNumber,
    players,
    onNotify, // snackbar global (recomendado)
}) => {
    const open = Boolean(anchorEl);

    const totalPuntos = playerData?.totalPuntos ?? 0;
    const totalFaltas = playerData?.totalFaltas ?? 0;
    const totalViolation = playerData?.totalViolation ?? 0;
    const totalAssist = playerData?.totalAssist ?? 0;
    const totalRebound = playerData?.totalRebound ?? 0;

    const canMinus = (n) => totalPuntos >= n;
    const canMinusFoul = () => totalFaltas >= 1;
    const canMinusViolation = () => totalViolation >= 1;
    const canMinusAssist = () => totalAssist >= 1;
    const canMinusRebound = () => totalRebound >= 1;

    const allowScoringButtons = playerData?.playing || playerData?.number === "team";

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
            <Box p={2} display="flex" flexDirection="column" alignItems="center">
                <Typography variant="h6">
                    {playerData?.name || "Sin nombre"} (#{playerNumber})
                </Typography>

                <Typography variant="body2">
                    Puntos: {totalPuntos} | Faltas: {totalFaltas}/5
                </Typography>

                {allowScoringButtons && (
                    <>
                        {/* +1 +2 +3 (long press resta) */}
                        <Box mt={2} mb={1} display="flex">
                            <LongPressButton
                                variant="contained"
                                style={{ margin: 5, fontSize: 18, padding: 15, minWidth: 56 }}
                                onClick={() => onScore("1", playerNumber)}
                                onLongPress={() => {
                                    if (canMinus(1)) {
                                        onScore("-1", playerNumber);
                                        onNotify?.(`Resta 1 punto a #${playerNumber}`);
                                    }
                                }}
                            >
                                +1
                            </LongPressButton>

                            <LongPressButton
                                variant="contained"
                                style={{ margin: 5, fontSize: 18, padding: 15, minWidth: 56 }}
                                onClick={() => onScore("2", playerNumber)}
                                onLongPress={() => {
                                    if (canMinus(2)) {
                                        onScore("-2", playerNumber);
                                        onNotify?.(`Resta 2 puntos a #${playerNumber}`);
                                    }
                                }}
                            >
                                +2
                            </LongPressButton>

                            <LongPressButton
                                variant="contained"
                                style={{ margin: 5, fontSize: 18, padding: 15, minWidth: 56 }}
                                onClick={() => onScore("3", playerNumber)}
                                onLongPress={() => {
                                    if (canMinus(3)) {
                                        onScore("-3", playerNumber);
                                        onNotify?.(`Resta 3 puntos a #${playerNumber}`);
                                    }
                                }}
                            >
                                +3
                            </LongPressButton>
                        </Box>

                        {/* Falta / Violación (long press quita) */}
                        <Box mb={1} display="flex">
                            <LongPressButton
                                variant="contained"
                                color="primary"
                                style={{ margin: 4, fontSize: 15, padding: 8 }}
                                onClick={() => onScore("foul", playerNumber)}
                                onLongPress={() => {
                                    if (canMinusFoul()) {
                                        onScore("-foul", playerNumber);
                                        onNotify?.(`Quitar falta a #${playerNumber}`);
                                    }
                                }}
                            >
                                Falta
                            </LongPressButton>
                            <LongPressButton
                                variant="outlined"
                                color="secondary"
                                style={{ margin: 4, fontSize: 15, padding: 8 }}
                                onClick={() => onScore("violation", playerNumber)}
                                onLongPress={() => {
                                    if (canMinusViolation()) {
                                        onScore("-violation", playerNumber);
                                        onNotify?.(`Quitar violación a #${playerNumber}`);
                                    }
                                }}
                            >
                                Violación
                            </LongPressButton>


                        </Box>
                        <Box mb={1} display="flex">


                            <LongPressButton
                                variant="outlined"
                                color="secondary"
                                style={{ margin: 4, fontSize: 15, padding: 8 }}
                                onClick={() => onScore("rebound", playerNumber)}
                                onLongPress={() => {
                                    if (canMinusRebound()) {
                                        onScore("-rebound", playerNumber);
                                        onNotify?.(`Quitar rebote a #${playerNumber}`);
                                    }
                                }}
                            >
                                Rebote
                            </LongPressButton>

                            <LongPressButton
                                variant="outlined"
                                color="secondary"
                                style={{ margin: 4, fontSize: 15, padding: 8 }}
                                onClick={() => onScore("assist", playerNumber)}
                                onLongPress={() => {
                                    if (canMinusAssist()) {
                                        onScore("-assist", playerNumber);
                                        onNotify?.(`Quitar asistencia a #${playerNumber}`);
                                    }
                                }}
                            >
                                Asistencia
                            </LongPressButton>
                        </Box>

                        <Box mb={1} display="flex">
                            <Typography variant="caption" style={{ alignSelf: "center", opacity: 0.7 }}>
                                (Mantén presionado para restar la anotación)
                            </Typography>
                        </Box>
                    </>
                )}

                {playerData?.playing && (
                    <Box>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => onScore("cambiosale", playerNumber)}
                            style={{ margin: 4, fontSize: 15, padding: 8 }}
                        >
                            Sale de cancha
                        </Button>
                    </Box>
                )}

                {!playerData?.playing && players && players[playerData?.teamKey] && (
                    <Box>
                        {Object.values(players[playerData.teamKey])
                            .filter((a) => a.playing)
                            .map((a) => (
                                <Button
                                    key={a.number}
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => onScore("cambio", playerNumber, a.number)}
                                    style={{ margin: 4, fontSize: 18 }}
                                >
                                    <Typography variant="subtitle1">Cambio por:</Typography>
                                    <Typography variant="h4">{a.number}</Typography>
                                </Button>
                            ))}

                        {Object.values(players[playerData.teamKey]).filter((p) => p.playing === true).length < 5 && (
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => onScore("cambio", playerNumber, "NA")}
                                style={{ margin: 5, fontSize: 18, padding: 15 }}
                            >
                                Entra a Cancha
                            </Button>
                        )}
                    </Box>
                )}
            </Box>
        </Popover>
    );
};

export default ScoringPopover;
